<template>
  <div
    class="file-upload-container"
    @dragenter="onDragenter"
    @dragover="onDragover"
    @dragleave="onDragleave"
    @drop="onDrop"
  >
    <div class="add-file-form">
      <span v-if="files.length === 0">
        <p v-if="language === 'en'">
          Drag and drop the file here, or click the “Select File” button to
          choose a file manually.
        </p>
        <p v-else>
          첨부할 파일을 여기에 끌어다 놓거나, 파일 선택 버튼을 눌러 파일을 직접
          선택해주세요.
        </p>
      </span>
      <button
        type="button"
        class="btn btn--medium_blue btn--m"
        @click.stop="onClick"
      >
        <i class="svg_icon icon_upload blue" aria-hidden="true"></i>
        <p v-if="language === 'en'">Select File</p>
        <p v-else>파일 선택</p>
      </button>
      <input
        type="file"
        ref="fileInput"
        @change="onFileChange"
        hidden
        multiple
      />

      <div v-if="files.length > 0" class="file-upload-list">
        <div
          class="file-upload-list__item"
          v-for="(file, index) in files"
          :key="index"
        >
          <div class="file-upload-list__item__data-name">
            {{ file.name }}
          </div>
          <div>
            {{ formatFileSize(file.size) }}
          </div>
          <button
            type="button"
            class="btn btn--s btn--gray"
            @click.stop="handleRemove(index)"
          >
            삭제
          </button>
        </div>
      </div>
    </div>
    <div v-if="language === 'en'">
      <p>
        ※ The size of the attached file must not exceed {{ maxSize }} MB. <br />
      </p>
      <p v-if="allowExt.length > 0">
        ※ Allowed file extensions for upload: {{ allowExt.join(", ") }}.
      </p>
    </div>
    <div v-else>
      <p>※ 첨부파일의 크기는 {{ maxSize }}MB이하로 제한됩니다 <br /></p>
      <p v-if="allowExt.length > 0">
        ※ 첨부 가능 확장자: {{ allowExt.join(", ") }} (최대 {{ maxSize }}MB)
      </p>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted } from "vue";

export default {
  props: {
    language: {
      type: String,
      default: "kr",
    },
    limit: {
      type: Number,
      default: 1,
    },
    maxSize: {
      type: Number,
      default: 30,
    },
    allowExt: {
      type: Array,
      default: [],
    },
    uploadFile: {
      type: Object,
      Array,
      default: null,
    },
  },
  emits: ["add"],
  setup(props, { emit, expose }) {
    // data 정의
    const files = ref([]);
    const fileInput = ref(null);
    const isDragged = ref(false);

    // methods 정의
    const onClick = () => {
      //첨부파일 창
      fileInput.value.click();
    };

    // 첨부 파일 변경 핸들러
    const onFileChange = (event) => {
      const selectedFiles = event.target.files;
      if (!selectedFiles || selectedFiles.length === 0) return;

      const MAX_FILE_SIZE_BYTES = props.maxSize * 1024 * 1024;
      const validFiles = [];

      for (const file of selectedFiles) {
        let fileName = file.name;
        let fileExt = fileName
          .substring(fileName.lastIndexOf(".") + 1, fileName.length)
          .toLowerCase();
        if (file.size > MAX_FILE_SIZE_BYTES) {
          alert(
            `파일 "${file.name}"의 크기가 ${props.maxSize}MB를 초과합니다.`
          );
          files.value = []; // 유효성 검사 실패 시 파일 목록 초기화
          return;
        }
        if (
          props.allowExt.length > 0 &&
          props.allowExt.indexOf(fileExt) == -1
        ) {
          alert("허용하지 않는 확장자입니다.");
          files.value = []; // 유효성 검사 실패 시 파일 목록 초기화
          return;
        }
        validFiles.push(file);
      }
      files.value = validFiles;
    };

    // 파일 사이즈 표현
    const formatFileSize = (bytes) => {
      if (bytes === 0) {
        return "0 B";
      }
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB", "TB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    watch(files, (newFiles) => {
      if (newFiles.length > props.limit) {
        alert(`${props.limit}개 까지만 선택이 가능합니다.`);
        files.value = [];
      } else {
        emit("add", files.value);
      }
    });

    const handleRemove = (index) => {
      const updated = [...files.value];
      updated.splice(index, 1);
      files.value = updated;
      fileInput.value.value = "";
    };

    const onDragenter = () => {
      isDragged.value = true;
    };

    const onDragleave = () => {
      isDragged.value = false;
    };

    const onDragover = (event) => {
      event.preventDefault(); // 드롭을 허용하도록 기본 동작 방지
    };

    const onDrop = (event) => {
      event.preventDefault(); // 링크 열기 등 기본 동작 방지
      isDragged.value = false;
      const droppedFiles = event.dataTransfer.files;
      onFileChange({ target: { files: droppedFiles } });
    };
    const reset = () => {
      //데이터 초기화
      files.value = [];
    };
    expose({ reset });

    // life cycle 정의
    onMounted(() => {
      if (Array.isArray(props.uploadFile)) {
        files.value = props.uploadFile;
      } else if (props.uploadFile && typeof props.uploadFile === "object") {
        files.value.push(props.uploadFile);
      }
    });

    //  html에서 사용될 데이터 정의
    return {
      files,
      onFileChange,
      onClick,
      formatFileSize,
      fileInput,
      handleRemove,
      onDragenter,
      onDragleave,
      onDragover,
      onDrop,
      isDragged,
    };
  },
};
</script>
